import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Store, 
  ShoppingCart, 
  Footprints, 
  Car, 
  Loader2,
  AlertCircle,
  ChevronRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate a simple device fingerprint for duplicate detection
const getDeviceFingerprint = () => {
  const stored = sessionStorage.getItem('device_fingerprint');
  if (stored) return stored;
  
  const fingerprint = `fp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('device_fingerprint', fingerprint);
  return fingerprint;
};

export default function VisitCapture() {
  const { qrCodeId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [qrInfo, setQrInfo] = useState(null);
  const [vehicleReg, setVehicleReg] = useState("");
  const [showRegInput, setShowRegInput] = useState(false);
  
  // Check for stored vehicle registration from previous visits
  useEffect(() => {
    const storedReg = localStorage.getItem('parkwise_vehicle_reg');
    if (storedReg) {
      setVehicleReg(storedReg);
    }
    
    const fetchQRInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API}/public/qr/${qrCodeId}`);
        setQrInfo(response.data);
      } catch (err) {
        console.error("Failed to fetch QR info:", err);
        if (err.response?.status === 404) {
          setError("This QR code is not recognized. Please try scanning again.");
        } else if (err.response?.status === 410) {
          setError(err.response?.data?.detail || "This QR code is no longer active.");
        } else {
          setError("Unable to load visit information. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchQRInfo();
  }, [qrCodeId]);

  const handleRecordVisit = async (includeReg = true) => {
    try {
      setSubmitting(true);
      
      const payload = {
        qr_code_id: qrCodeId,
        device_fingerprint: getDeviceFingerprint()
      };
      
      if (includeReg && vehicleReg.trim()) {
        payload.vehicle_registration = vehicleReg.trim().toUpperCase();
        // Store for future visits
        localStorage.setItem('parkwise_vehicle_reg', payload.vehicle_registration);
      }
      
      const response = await axios.post(`${API}/visits`, payload);
      
      // Navigate to success page with result
      navigate('/visit/success', { 
        state: { 
          result: response.data,
          qrInfo: qrInfo
        },
        replace: true
      });
    } catch (err) {
      console.error("Failed to record visit:", err);
      setError(err.response?.data?.detail || "Failed to record visit. Please try again.");
      setSubmitting(false);
    }
  };

  const handleContinueAnonymous = () => {
    handleRecordVisit(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800/80 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-white mb-2">Something went wrong</h1>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPOS = qrInfo?.visit_type === 'pos';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col" data-testid="visit-capture-page">
      {/* Header with branding */}
      <header className="p-4 flex items-center justify-between border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-medium text-slate-400">ParkWise</span>
        </div>
        <span className="text-xs text-slate-500">Local Connect</span>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Retailer Card */}
        <Card className="w-full max-w-md bg-slate-800/80 border-slate-700 mb-6">
          <CardContent className="pt-6 pb-6">
            {/* Retailer info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                {qrInfo.retailer_logo ? (
                  <img 
                    src={qrInfo.retailer_logo} 
                    alt={qrInfo.retailer_name} 
                    className="w-12 h-12 object-contain"
                  />
                ) : (
                  <Store className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-white truncate" data-testid="retailer-name">
                  {qrInfo.retailer_name}
                </h1>
                <p className="text-sm text-slate-400">{qrInfo.site_name}</p>
                {qrInfo.retailer_category && (
                  <span className="text-xs text-slate-500">{qrInfo.retailer_category}</span>
                )}
              </div>
            </div>

            {/* Visit type indicator */}
            <div 
              className={`flex items-center gap-3 p-4 rounded-xl mb-6 ${
                isPOS 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-purple-500/10 border border-purple-500/30'
              }`}
              data-testid="visit-type-badge"
            >
              {isPOS ? (
                <ShoppingCart className="w-6 h-6 text-green-400" />
              ) : (
                <Footprints className="w-6 h-6 text-purple-400" />
              )}
              <div>
                <p className={`font-semibold ${isPOS ? 'text-green-400' : 'text-purple-400'}`}>
                  {isPOS ? 'POS / Validation QR' : 'Browse QR'}
                </p>
                <p className="text-xs text-slate-400">
                  {isPOS 
                    ? 'Scan after your purchase to earn 3 hours free parking' 
                    : 'Thanks for browsing! Scan to record your visit'}
                </p>
              </div>
            </div>

            {/* Ayr Miles Info Banner (for POS) */}
            {isPOS && qrInfo.is_ayr_miles_partner && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-purple-500/30 flex items-center justify-center">
                    <span className="text-xs">✨</span>
                  </div>
                  <p className="text-purple-400 font-semibold text-sm">Ayr Miles Partner</p>
                </div>
                <p className="text-xs text-slate-400">
                  Get your first 3 hours of parking free when you shop here. Valid same day only.
                </p>
              </div>
            )}

            {/* Vehicle registration section */}
            {!showRegInput ? (
              <div className="space-y-3">
                {vehicleReg ? (
                  <>
                    <div className="p-4 bg-slate-700/50 rounded-xl">
                      <p className="text-sm text-slate-400 mb-1">Your vehicle</p>
                      <p className="text-lg font-mono font-bold text-white">{vehicleReg}</p>
                    </div>
                    <Button
                      onClick={() => handleRecordVisit(true)}
                      disabled={submitting}
                      className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                      data-testid="record-visit-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Record Visit
                          <ChevronRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                    <button
                      onClick={() => setShowRegInput(true)}
                      className="w-full text-sm text-slate-400 hover:text-white py-2"
                    >
                      Change vehicle
                    </button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => setShowRegInput(true)}
                      className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                      data-testid="enter-vehicle-btn"
                    >
                      Enter Vehicle Registration
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                    <button
                      onClick={handleContinueAnonymous}
                      disabled={submitting}
                      className="w-full text-sm text-slate-400 hover:text-white py-3"
                      data-testid="continue-anonymous-btn"
                    >
                      {submitting ? 'Recording...' : 'Continue without registration'}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300 mb-2 block">Vehicle Registration</Label>
                  <Input
                    value={vehicleReg}
                    onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
                    placeholder="e.g. AB12 CDE"
                    className="h-14 text-lg font-mono bg-slate-700 border-slate-600 text-white text-center tracking-wider"
                    autoFocus
                    data-testid="vehicle-reg-input"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Enter your vehicle registration to earn parking credits
                  </p>
                </div>
                <Button
                  onClick={() => handleRecordVisit(true)}
                  disabled={submitting || !vehicleReg.trim()}
                  className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-visit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Record Visit
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowRegInput(false)}
                    className="flex-1 text-sm text-slate-400 hover:text-white py-2"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleContinueAnonymous}
                    disabled={submitting}
                    className="flex-1 text-sm text-slate-400 hover:text-white py-2"
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info text */}
        <p className="text-xs text-slate-500 text-center max-w-xs">
          Scanning records your visit and helps you earn parking credits at {qrInfo.site_name}
        </p>
      </main>
    </div>
  );
}
